// Clinical QA Report Analyzer - Hospice CareLens
// AI-powered DYNAMIC analysis of ANY clinical report

import React, { useState, useEffect, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { 
  Brain, 
  AlertTriangle, 
  CheckCircle2, 
  Clock, 
  Target,
  FileText,
  Upload,
  Users,
  TrendingUp,
  TrendingDown,
  ChevronRight,
  ChevronDown,
  Loader2,
  BarChart3,
  Activity,
  Shield,
  Heart,
  Droplet,
  AlertCircle,
  Thermometer,
  FileSpreadsheet,
  Download,
  RefreshCw,
  Sparkles,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  Zap,
  ClipboardList,
  PieChart,
  Database,
  Eye,
  Layers,
  Hash,
  TrendingDown as TrendDown,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category configurations
const CATEGORY_CONFIG = {
  wounds: {
    icon: Droplet,
    color: 'from-rose-500 to-red-600',
    bgColor: 'bg-rose-500/10',
    textColor: 'text-rose-400',
    borderColor: 'border-rose-500/30'
  },
  falls: {
    icon: AlertTriangle,
    color: 'from-amber-500 to-orange-600',
    bgColor: 'bg-amber-500/10',
    textColor: 'text-amber-400',
    borderColor: 'border-amber-500/30'
  },
  infections: {
    icon: Thermometer,
    color: 'from-purple-500 to-violet-600',
    bgColor: 'bg-purple-500/10',
    textColor: 'text-purple-400',
    borderColor: 'border-purple-500/30'
  },
  pain: {
    icon: Heart,
    color: 'from-blue-500 to-indigo-600',
    bgColor: 'bg-blue-500/10',
    textColor: 'text-blue-400',
    borderColor: 'border-blue-500/30'
  }
};

const SEVERITY_COLORS = {
  critical: 'bg-red-500/20 text-red-400 border-red-500/30',
  high: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  medium: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  low: 'bg-blue-500/20 text-blue-400 border-blue-500/30'
};

export default function ClinicalQAAnalyzer() {
  const [activeTab, setActiveTab] = useState('upload');
  const [categories, setCategories] = useState({});
  const [analysisHistory, setAnalysisHistory] = useState([]);
  const [currentAnalysis, setCurrentAnalysis] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    metrics: true,
    teams: true,
    insights: true,
    recommendations: true
  });

  const token = localStorage.getItem('qapi-token');
  const headers = {
    'Authorization': `Bearer ${token}`
  };

  useEffect(() => {
    fetchCategories();
    fetchHistory();
  }, []);

  const fetchCategories = async () => {
    try {
      const res = await fetch(`${API_URL}/api/clinical-qa/categories`, {
        headers: { ...headers, 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (data.success) {
        setCategories(data.categories);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchHistory = async () => {
    try {
      const res = await fetch(`${API_URL}/api/clinical-qa/history`, {
        headers: { ...headers, 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (data.success) {
        setAnalysisHistory(data.analyses);
      }
    } catch (error) {
      console.error('Failed to fetch history:', error);
    }
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    setUploading(true);
    setCurrentAnalysis(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await fetch(`${API_URL}/api/clinical-qa/analyze`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const data = await res.json();

      if (data.success) {
        setCurrentAnalysis(data.analysis);
        setActiveTab('results');
        toast.success(`Analysis complete! ${data.analysis.record_count} records analyzed.`);
        fetchHistory();
      } else {
        toast.error(data.detail || 'Analysis failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload and analyze file');
    } finally {
      setUploading(false);
    }
  }, [token]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/pdf': ['.pdf']
    },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024
  });

  const loadAnalysis = async (analysisId) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/clinical-qa/${analysisId}`, {
        headers: { ...headers, 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (data.success) {
        setCurrentAnalysis(data.analysis);
        setActiveTab('results');
      }
    } catch (error) {
      toast.error('Failed to load analysis');
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = async (analysisId) => {
    setDownloadingPdf(analysisId);
    try {
      const res = await fetch(`${API_URL}/api/clinical-qa/${analysisId}/export/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `ClinicalQA_${analysisId}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('PDF downloaded successfully');
      } else {
        toast.error('Failed to download PDF');
      }
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error('Failed to download PDF');
    } finally {
      setDownloadingPdf(null);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const renderMetricCard = (label, value, benchmark, unit = '', inverse = false) => {
    // Handle case where value is an object with {value, type, description}
    const displayValue = typeof value === 'object' && value !== null ? value.value : value;
    const displayBenchmark = typeof benchmark === 'object' && benchmark !== null ? benchmark.value : benchmark;
    
    const numValue = parseFloat(displayValue) || 0;
    const numBenchmark = parseFloat(displayBenchmark) || 0;
    const isBetter = inverse ? numValue < numBenchmark : numValue <= numBenchmark;
    const variance = numBenchmark > 0 ? ((numValue - numBenchmark) / numBenchmark * 100).toFixed(1) : 0;
    
    // Determine unit from object type if available
    const displayUnit = (typeof value === 'object' && value?.type === 'percentage') ? '%' : unit;
    
    return (
      <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
        <p className="text-slate-400 text-sm">{label}</p>
        <div className="flex items-end justify-between mt-1">
          <p className="text-2xl font-black text-white">{displayValue}{displayUnit}</p>
          <div className="flex items-center gap-1">
            {isBetter ? (
              <CheckCircle2 className="h-4 w-4 text-emerald-400" />
            ) : (
              <AlertCircle className="h-4 w-4 text-amber-400" />
            )}
            <span className={`text-xs ${isBetter ? 'text-emerald-400' : 'text-amber-400'}`}>
              {variance > 0 ? '+' : ''}{variance}%
            </span>
          </div>
        </div>
        <p className="text-xs text-slate-500 mt-1">Benchmark: {displayBenchmark}{displayUnit}</p>
      </div>
    );
  };

  const renderCategoryAnalysis = () => {
    if (!currentAnalysis) return null;
    
    const { detected_category, category_analysis, category_name } = currentAnalysis;
    const config = CATEGORY_CONFIG[detected_category] || CATEGORY_CONFIG.wounds;
    const Icon = config.icon;
    const benchmarks = categories[detected_category]?.benchmarks || {};

    return (
      <Card className={`bg-slate-900/80 border-2 ${config.borderColor}`}>
        <CardHeader className="cursor-pointer" onClick={() => toggleSection('metrics')}>
          <CardTitle className="flex items-center justify-between text-white">
            <div className="flex items-center gap-2">
              <div className={`p-2 rounded-lg bg-gradient-to-br ${config.color}`}>
                <Icon className="h-5 w-5 text-white" />
              </div>
              {category_name} Analysis
            </div>
            {expandedSections.metrics ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
          </CardTitle>
          <CardDescription>
            {category_analysis?.total_incidents || 0} incidents analyzed
          </CardDescription>
        </CardHeader>
        {expandedSections.metrics && (
          <CardContent>
            {/* Key Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {Object.entries(category_analysis?.key_metrics || {}).map(([key, value]) => {
                const benchmarkKey = key;
                const benchmark = benchmarks[benchmarkKey];
                const isRate = key.toLowerCase().includes('rate');
                const isInverse = key.toLowerCase().includes('worsening') || 
                                 key.toLowerCase().includes('injury') ||
                                 key.toLowerCase().includes('breakthrough');
                return (
                  <div key={key}>
                    {renderMetricCard(
                      key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                      value,
                      benchmark || value,
                      isRate ? '%' : '',
                      isInverse
                    )}
                  </div>
                );
              })}
            </div>

            {/* Distribution Charts */}
            {detected_category === 'falls' && category_analysis?.severity_distribution && (
              <div className="space-y-4">
                <h4 className="text-sm font-bold text-slate-300">Injury Severity Distribution</h4>
                <div className="grid grid-cols-4 gap-2">
                  {Object.entries(category_analysis.severity_distribution).map(([severity, count]) => (
                    <div key={severity} className="text-center">
                      <div className={`h-24 rounded-lg flex items-end justify-center pb-2 ${
                        severity === 'major' ? 'bg-red-500/20' :
                        severity === 'moderate' ? 'bg-orange-500/20' :
                        severity === 'minor' ? 'bg-amber-500/20' : 'bg-emerald-500/20'
                      }`}>
                        <div 
                          className={`w-full mx-2 rounded-t ${
                            severity === 'major' ? 'bg-red-500' :
                            severity === 'moderate' ? 'bg-orange-500' :
                            severity === 'minor' ? 'bg-amber-500' : 'bg-emerald-500'
                          }`}
                          style={{ 
                            height: `${Math.max(10, (count / Math.max(...Object.values(category_analysis.severity_distribution))) * 100)}%` 
                          }}
                        />
                      </div>
                      <p className="text-xs text-slate-400 mt-1 capitalize">{severity}</p>
                      <p className="text-sm font-bold text-white">{count}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {detected_category === 'pain' && category_analysis?.pain_score_distribution && (
              <div className="space-y-4">
                <h4 className="text-sm font-bold text-slate-300">Pain Score Distribution</h4>
                <div className="grid grid-cols-3 gap-3">
                  {Object.entries(category_analysis.pain_score_distribution).map(([range, count]) => (
                    <div key={range} className={`p-4 rounded-lg text-center ${
                      range.includes('mild') ? 'bg-emerald-500/10 border border-emerald-500/30' :
                      range.includes('moderate') ? 'bg-amber-500/10 border border-amber-500/30' :
                      'bg-red-500/10 border border-red-500/30'
                    }`}>
                      <p className="text-2xl font-black text-white">{count}</p>
                      <p className="text-xs text-slate-400">{range}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Top Locations/Factors */}
            {category_analysis?.top_locations && Object.keys(category_analysis.top_locations).length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-bold text-slate-300 mb-2">Top Locations</h4>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(category_analysis.top_locations).map(([loc, count]) => (
                    <Badge key={loc} variant="outline" className="bg-slate-800/50">
                      {loc} <span className="ml-1 text-slate-500">({count})</span>
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        )}
      </Card>
    );
  };

  const renderTeamAnalysis = () => {
    if (!currentAnalysis?.team_analysis) return null;
    
    const { by_team, summary } = currentAnalysis.team_analysis;

    return (
      <Card className="bg-slate-900/80 border-slate-700/50">
        <CardHeader className="cursor-pointer" onClick={() => toggleSection('teams')}>
          <CardTitle className="flex items-center justify-between text-white">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-cyan-400" />
              Team Breakdown
            </div>
            {expandedSections.teams ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
          </CardTitle>
          <CardDescription>
            {summary?.total_teams || 0} teams, {summary?.total_clinicians || 0} clinicians
          </CardDescription>
        </CardHeader>
        {expandedSections.teams && (
          <CardContent>
            <div className="space-y-4">
              {/* Outlier Alert */}
              {summary?.outlier_teams?.length > 0 && (
                <Alert className="bg-amber-500/10 border-amber-500/30">
                  <AlertTriangle className="h-4 w-4 text-amber-400" />
                  <AlertTitle className="text-amber-400">Teams with High Incident Rates</AlertTitle>
                  <AlertDescription className="text-slate-300">
                    {summary.outlier_teams.map(t => `${t.team} (+${t.variance}%)`).join(', ')}
                  </AlertDescription>
                </Alert>
              )}

              {/* Team Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(by_team || {}).map(([team, data]) => {
                  const isOutlier = summary?.outlier_teams?.some(t => t.team === team);
                  return (
                    <div 
                      key={team} 
                      className={`p-4 rounded-lg border ${
                        isOutlier ? 'bg-amber-500/10 border-amber-500/30' : 'bg-slate-800/50 border-slate-700/50'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-bold text-white">{team}</h4>
                        {isOutlier && (
                          <Badge className="bg-amber-500/20 text-amber-400">Outlier</Badge>
                        )}
                      </div>
                      <div className="flex items-end justify-between">
                        <div>
                          <p className="text-3xl font-black text-white">{data.count}</p>
                          <p className="text-xs text-slate-500">incidents</p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-slate-300">{data.percentage}%</p>
                          <p className="text-xs text-slate-500">{data.clinicians?.length || 0} clinicians</p>
                        </div>
                      </div>
                      <Progress value={data.percentage} className="mt-2 h-1" />
                    </div>
                  );
                })}
              </div>
            </div>
          </CardContent>
        )}
      </Card>
    );
  };

  const renderAIInsights = () => {
    if (!currentAnalysis?.ai_insights) return null;
    
    const { summary, key_findings, areas_of_concern, positive_trends, ai_generated } = currentAnalysis.ai_insights;

    return (
      <Card className="bg-slate-900/80 border-slate-700/50">
        <CardHeader className="cursor-pointer" onClick={() => toggleSection('insights')}>
          <CardTitle className="flex items-center justify-between text-white">
            <div className="flex items-center gap-2">
              <Brain className="h-5 w-5 text-fuchsia-400" />
              AI Insights
              {ai_generated && (
                <Badge className="bg-fuchsia-500/20 text-fuchsia-400 ml-2">GPT-5.2</Badge>
              )}
            </div>
            {expandedSections.insights ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
          </CardTitle>
        </CardHeader>
        {expandedSections.insights && (
          <CardContent className="space-y-4">
            {/* Summary */}
            <Alert className="bg-fuchsia-500/10 border-fuchsia-500/30">
              <Sparkles className="h-4 w-4 text-fuchsia-400" />
              <AlertTitle className="text-fuchsia-400">Executive Summary</AlertTitle>
              <AlertDescription className="text-slate-300">
                {summary}
              </AlertDescription>
            </Alert>

            {/* Areas of Concern */}
            {areas_of_concern?.length > 0 && (
              <div>
                <h4 className="text-sm font-bold text-red-400 mb-2 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  Areas of Concern
                </h4>
                <div className="space-y-2">
                  {areas_of_concern.map((concern, idx) => (
                    <div 
                      key={idx} 
                      className={`p-3 rounded-lg border ${SEVERITY_COLORS[concern.severity] || SEVERITY_COLORS.medium}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-bold text-white">{concern.area}</span>
                        <Badge className={SEVERITY_COLORS[concern.severity]}>
                          {concern.severity}
                        </Badge>
                      </div>
                      <p className="text-slate-300 text-sm">{concern.details}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Positive Trends */}
            {positive_trends?.length > 0 && (
              <div>
                <h4 className="text-sm font-bold text-emerald-400 mb-2 flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  Positive Trends
                </h4>
                <div className="space-y-2">
                  {positive_trends.map((trend, idx) => (
                    <div 
                      key={idx} 
                      className="p-3 rounded-lg border bg-emerald-500/10 border-emerald-500/30"
                    >
                      <p className="text-emerald-300">{trend}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        )}
      </Card>
    );
  };

  const renderRecommendations = () => {
    if (!currentAnalysis?.recommendations?.length) return null;

    return (
      <Card className="bg-slate-900/80 border-slate-700/50">
        <CardHeader className="cursor-pointer" onClick={() => toggleSection('recommendations')}>
          <CardTitle className="flex items-center justify-between text-white">
            <div className="flex items-center gap-2">
              <Target className="h-5 w-5 text-emerald-400" />
              Recommendations
            </div>
            {expandedSections.recommendations ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
          </CardTitle>
        </CardHeader>
        {expandedSections.recommendations && (
          <CardContent>
            <div className="space-y-3">
              {currentAnalysis.recommendations.map((rec, idx) => (
                <div 
                  key={idx} 
                  className="p-4 rounded-lg bg-slate-800/50 border border-slate-700/50"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold text-white">{rec.area}</span>
                    <Badge className={SEVERITY_COLORS[rec.priority]}>
                      {rec.priority} priority
                    </Badge>
                  </div>
                  <p className="text-slate-300">{rec.recommendation}</p>
                  <div className="flex items-center gap-2 mt-2 text-sm text-slate-500">
                    <Clock className="h-4 w-4" />
                    {rec.timeline}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        )}
      </Card>
    );
  };

  // NEW: Dynamic Analysis Display Components
  const renderDynamicAnalysis = () => {
    if (!currentAnalysis?.dynamic_analysis) return null;
    
    const { data_quality, distributions, statistics, key_metrics, severity_distribution } = currentAnalysis.dynamic_analysis;

    return (
      <Card className="bg-slate-900/80 border-slate-700/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white">
            <Layers className="h-5 w-5 text-cyan-400" />
            Dynamic Data Analysis
            {currentAnalysis.ai_understood && (
              <Badge className="bg-cyan-500/20 text-cyan-400 ml-2">AI-Powered</Badge>
            )}
          </CardTitle>
          <CardDescription>
            Analysis tailored to your document's actual content
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Data Quality */}
          {data_quality && (
            <div>
              <h4 className="text-sm font-bold text-slate-300 mb-3 flex items-center gap-2">
                <Database className="h-4 w-4 text-blue-400" />
                Data Quality Assessment
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50 text-center">
                  <p className="text-2xl font-black text-white">{data_quality.overall_completeness || 0}%</p>
                  <p className="text-xs text-slate-500">Overall Completeness</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50 text-center">
                  <p className="text-2xl font-black text-cyan-400">{data_quality.total_fields_detected || 0}</p>
                  <p className="text-xs text-slate-500">Fields Detected</p>
                </div>
              </div>
            </div>
          )}

          {/* Key Metrics from Dynamic Analysis */}
          {key_metrics && Object.keys(key_metrics).length > 0 && (
            <div>
              <h4 className="text-sm font-bold text-slate-300 mb-3 flex items-center gap-2">
                <Hash className="h-4 w-4 text-emerald-400" />
                Key Metrics Detected
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {Object.entries(key_metrics).map(([name, data]) => (
                  <div key={name} className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
                    <p className="text-xs text-slate-500 mb-1">{name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                    <p className="text-xl font-black text-white">
                      {typeof data === 'object' ? data.value : data}
                      {typeof data === 'object' && data.type === 'percentage' && '%'}
                    </p>
                    {typeof data === 'object' && data.benchmark && (
                      <p className="text-xs text-slate-500">Benchmark: {data.benchmark}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Severity Distribution */}
          {severity_distribution && Object.keys(severity_distribution).length > 0 && (
            <div>
              <h4 className="text-sm font-bold text-slate-300 mb-3 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-400" />
                Severity Distribution
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {Object.entries(severity_distribution).map(([level, count]) => {
                  const isHighRisk = level.includes('high') || level.includes('critical') || level.includes('severe') || level.includes('major');
                  return (
                    <div 
                      key={level} 
                      className={`p-3 rounded-lg text-center ${
                        isHighRisk ? 'bg-red-500/10 border border-red-500/30' : 'bg-slate-800/50 border border-slate-700/50'
                      }`}
                    >
                      <p className={`text-2xl font-black ${isHighRisk ? 'text-red-400' : 'text-white'}`}>{count}</p>
                      <p className="text-xs text-slate-400 capitalize">{level}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Statistical Analysis */}
          {statistics && Object.keys(statistics).length > 0 && (
            <div>
              <h4 className="text-sm font-bold text-slate-300 mb-3 flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-purple-400" />
                Statistical Analysis
              </h4>
              <div className="space-y-3">
                {Object.entries(statistics).slice(0, 5).map(([column, stats]) => (
                  <div key={column} className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
                    <p className="text-sm font-bold text-white mb-2 capitalize">{column.replace(/_/g, ' ')}</p>
                    <div className="grid grid-cols-5 gap-2 text-center">
                      <div>
                        <p className="text-lg font-bold text-cyan-400">{stats.min}</p>
                        <p className="text-xs text-slate-500">Min</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold text-blue-400">{stats.mean}</p>
                        <p className="text-xs text-slate-500">Mean</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold text-purple-400">{stats.median}</p>
                        <p className="text-xs text-slate-500">Median</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold text-pink-400">{stats.max}</p>
                        <p className="text-xs text-slate-500">Max</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold text-emerald-400">{stats.count}</p>
                        <p className="text-xs text-slate-500">Count</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Distributions */}
          {distributions && Object.keys(distributions).length > 0 && (
            <div>
              <h4 className="text-sm font-bold text-slate-300 mb-3 flex items-center gap-2">
                <PieChart className="h-4 w-4 text-amber-400" />
                Value Distributions
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(distributions).slice(0, 4).map(([column, data]) => (
                  <div key={column} className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-bold text-white capitalize">{column.replace(/_/g, ' ')}</p>
                      <Badge variant="outline" className="text-xs">{data.unique_count} unique</Badge>
                    </div>
                    <div className="space-y-1">
                      {Object.entries(data.values || {}).slice(0, 5).map(([value, count]) => {
                        const total = Object.values(data.values || {}).reduce((a, b) => a + b, 0);
                        const pct = total > 0 ? (count / total * 100) : 0;
                        return (
                          <div key={value} className="flex items-center gap-2">
                            <div className="flex-1">
                              <div className="flex justify-between text-xs mb-1">
                                <span className="text-slate-300 truncate max-w-[150px]">{value}</span>
                                <span className="text-slate-500">{count} ({pct.toFixed(0)}%)</span>
                              </div>
                              <Progress value={pct} className="h-1" />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  // Enhanced AI Insights for dynamic analysis
  const renderEnhancedAIInsights = () => {
    if (!currentAnalysis?.ai_insights) return null;
    
    const { 
      summary, 
      key_findings, 
      areas_of_concern, 
      positive_trends,
      patterns_detected,
      team_insights,
      priority_actions,
      monitoring_recommendations,
      data_quality_notes,
      ai_generated 
    } = currentAnalysis.ai_insights;

    return (
      <Card className="bg-slate-900/80 border-slate-700/50">
        <CardHeader className="cursor-pointer" onClick={() => toggleSection('insights')}>
          <CardTitle className="flex items-center justify-between text-white">
            <div className="flex items-center gap-2">
              <Brain className="h-5 w-5 text-fuchsia-400" />
              AI Insights
              {ai_generated && (
                <Badge className="bg-fuchsia-500/20 text-fuchsia-400 ml-2">GPT-5.2</Badge>
              )}
            </div>
            {expandedSections.insights ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
          </CardTitle>
        </CardHeader>
        {expandedSections.insights && (
          <CardContent className="space-y-4">
            {/* Executive Summary */}
            {summary && (
              <Alert className="bg-fuchsia-500/10 border-fuchsia-500/30">
                <Sparkles className="h-4 w-4 text-fuchsia-400" />
                <AlertTitle className="text-fuchsia-400">Executive Summary</AlertTitle>
                <AlertDescription className="text-slate-300">
                  {summary}
                </AlertDescription>
              </Alert>
            )}

            {/* Key Findings */}
            {key_findings?.length > 0 && (
              <div>
                <h4 className="text-sm font-bold text-blue-400 mb-2 flex items-center gap-2">
                  <Eye className="h-4 w-4" />
                  Key Findings
                </h4>
                <div className="space-y-2">
                  {key_findings.map((finding, idx) => (
                    <div key={idx} className="flex items-start gap-2 p-2 rounded bg-blue-500/10 border border-blue-500/20">
                      <CheckCircle className="h-4 w-4 text-blue-400 mt-0.5 flex-shrink-0" />
                      <p className="text-slate-300 text-sm">{typeof finding === 'object' ? finding.finding : finding}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Patterns Detected */}
            {patterns_detected?.length > 0 && (
              <div>
                <h4 className="text-sm font-bold text-purple-400 mb-2 flex items-center gap-2">
                  <Layers className="h-4 w-4" />
                  Patterns Detected
                </h4>
                <div className="space-y-2">
                  {patterns_detected.map((pattern, idx) => (
                    <div key={idx} className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/30">
                      <p className="text-white font-medium">{pattern.pattern}</p>
                      {pattern.implication && (
                        <p className="text-slate-400 text-sm mt-1">{pattern.implication}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Areas of Concern */}
            {areas_of_concern?.length > 0 && (
              <div>
                <h4 className="text-sm font-bold text-red-400 mb-2 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  Areas of Concern
                </h4>
                <div className="space-y-2">
                  {areas_of_concern.map((concern, idx) => (
                    <div 
                      key={idx} 
                      className={`p-3 rounded-lg border ${SEVERITY_COLORS[concern.severity] || SEVERITY_COLORS.medium}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-bold text-white">{concern.area}</span>
                        <Badge className={SEVERITY_COLORS[concern.severity]}>
                          {concern.severity}
                        </Badge>
                      </div>
                      <p className="text-slate-300 text-sm">{concern.details}</p>
                      {concern.recommended_action && (
                        <p className="text-emerald-400 text-sm mt-2 flex items-center gap-1">
                          <Target className="h-3 w-3" />
                          {concern.recommended_action}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Positive Observations */}
            {positive_trends?.length > 0 && (
              <div>
                <h4 className="text-sm font-bold text-emerald-400 mb-2 flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  Positive Observations
                </h4>
                <div className="space-y-2">
                  {positive_trends.map((trend, idx) => (
                    <div 
                      key={idx} 
                      className="p-3 rounded-lg border bg-emerald-500/10 border-emerald-500/30"
                    >
                      <p className="text-emerald-300">{typeof trend === 'object' ? trend.observation : trend}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Team Insights */}
            {team_insights?.length > 0 && (
              <div>
                <h4 className="text-sm font-bold text-cyan-400 mb-2 flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Team Insights
                </h4>
                <div className="space-y-1">
                  {team_insights.map((insight, idx) => (
                    <p key={idx} className="text-slate-300 text-sm flex items-start gap-2">
                      <span className="text-cyan-400">•</span>
                      {insight}
                    </p>
                  ))}
                </div>
              </div>
            )}

            {/* Monitoring Recommendations */}
            {monitoring_recommendations?.length > 0 && (
              <div>
                <h4 className="text-sm font-bold text-amber-400 mb-2 flex items-center gap-2">
                  <Activity className="h-4 w-4" />
                  Monitoring Recommendations
                </h4>
                <div className="flex flex-wrap gap-2">
                  {monitoring_recommendations.map((rec, idx) => (
                    <Badge key={idx} variant="outline" className="bg-amber-500/10 border-amber-500/30 text-amber-300">
                      {rec}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Data Quality Notes */}
            {data_quality_notes?.length > 0 && (
              <Alert className="bg-slate-800/50 border-slate-700/50">
                <Database className="h-4 w-4 text-slate-400" />
                <AlertTitle className="text-slate-300">Data Quality Notes</AlertTitle>
                <AlertDescription className="text-slate-500 text-sm">
                  {data_quality_notes.join(' | ')}
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        )}
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-slate-950 p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-3 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600">
            <FileSpreadsheet className="h-8 w-8 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-black text-white">Clinical QA Analyzer</h1>
            <p className="text-slate-400">AI-Powered Dynamic Report Analysis</p>
          </div>
        </div>
        <p className="text-slate-500 mt-2 max-w-2xl">
          Upload <strong className="text-slate-300">any clinical report</strong> - our AI automatically understands your document's 
          structure and generates relevant analysis, metrics, and actionable recommendations.
        </p>
      </div>

      {/* Category Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {Object.entries(CATEGORY_CONFIG).map(([cat, config]) => {
          const Icon = config.icon;
          const catData = categories[cat];
          return (
            <Card key={cat} className={`bg-slate-900/80 border ${config.borderColor}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${config.bgColor}`}>
                    <Icon className={`h-5 w-5 ${config.textColor}`} />
                  </div>
                  <div>
                    <p className="text-white font-bold">{catData?.name || cat}</p>
                    <p className="text-xs text-slate-500">{catData?.metrics?.length || 0} metrics tracked</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-800/50 border border-slate-700/50 mb-6">
          <TabsTrigger value="upload" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-cyan-500 data-[state=active]:to-blue-600">
            <Upload className="h-4 w-4 mr-2" />
            Upload Report
          </TabsTrigger>
          <TabsTrigger value="results" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-fuchsia-500 data-[state=active]:to-purple-600" disabled={!currentAnalysis}>
            <BarChart3 className="h-4 w-4 mr-2" />
            Analysis Results
          </TabsTrigger>
          <TabsTrigger value="history" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-emerald-500 data-[state=active]:to-teal-600">
            <ClipboardList className="h-4 w-4 mr-2" />
            History
          </TabsTrigger>
        </TabsList>

        {/* Upload Tab */}
        <TabsContent value="upload">
          <Card className="bg-slate-900/80 border-slate-700/50">
            <CardContent className="p-8">
              <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-all ${
                  isDragActive 
                    ? 'border-cyan-500 bg-cyan-500/10' 
                    : 'border-slate-700 hover:border-slate-600 hover:bg-slate-800/30'
                }`}
              >
                <input {...getInputProps()} data-testid="file-upload-input" />
                {uploading ? (
                  <div className="flex flex-col items-center">
                    <Loader2 className="h-16 w-16 text-cyan-500 animate-spin mb-4" />
                    <p className="text-xl font-bold text-white">Analyzing Report...</p>
                    <p className="text-slate-400 mt-2">AI is extracting data and generating insights</p>
                  </div>
                ) : (
                  <>
                    <Upload className="h-16 w-16 text-slate-600 mx-auto mb-4" />
                    <p className="text-xl font-bold text-white mb-2">
                      {isDragActive ? 'Drop your report here' : 'Drag & drop your clinical report'}
                    </p>
                    <p className="text-slate-400 mb-4">or click to browse</p>
                    <div className="flex flex-wrap justify-center gap-2">
                      <Badge variant="outline" className="bg-slate-800/50">CSV</Badge>
                      <Badge variant="outline" className="bg-slate-800/50">Excel (.xlsx, .xls)</Badge>
                      <Badge variant="outline" className="bg-slate-800/50">PDF</Badge>
                    </div>
                    <p className="text-xs text-slate-500 mt-4">Maximum file size: 10MB</p>
                  </>
                )}
              </div>

              {/* File Format Help */}
              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
                <Alert className="bg-slate-800/50 border-slate-700/50">
                  <FileText className="h-4 w-4 text-slate-400" />
                  <AlertTitle className="text-slate-300">Supported Columns</AlertTitle>
                  <AlertDescription className="text-slate-500 text-sm">
                    patient_id, date, team, clinician, plus category-specific fields 
                    (stage, injury_severity, infection_type, pain_score, etc.)
                  </AlertDescription>
                </Alert>
                <Alert className="bg-slate-800/50 border-slate-700/50">
                  <Zap className="h-4 w-4 text-slate-400" />
                  <AlertTitle className="text-slate-300">Auto-Detection</AlertTitle>
                  <AlertDescription className="text-slate-500 text-sm">
                    The system automatically detects report type (wounds, falls, infections, pain) 
                    based on column headers and content.
                  </AlertDescription>
                </Alert>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Results Tab */}
        <TabsContent value="results">
          {currentAnalysis ? (
            <div className="space-y-6">
              {/* Analysis Header */}
              <Card className="bg-gradient-to-br from-cyan-500/20 to-blue-600/20 border-cyan-500/30">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className="bg-cyan-500/20 text-cyan-400">
                          {currentAnalysis.analysis_id}
                        </Badge>
                        {currentAnalysis.ai_understood && (
                          <Badge className="bg-fuchsia-500/20 text-fuchsia-400">
                            AI-Understood
                          </Badge>
                        )}
                        {currentAnalysis.confidence_score && (
                          <Badge className="bg-emerald-500/20 text-emerald-400">
                            {Math.round(currentAnalysis.confidence_score * 100)}% Confidence
                          </Badge>
                        )}
                      </div>
                      <h2 className="text-2xl font-black text-white">
                        {currentAnalysis.category_name}
                      </h2>
                      <p className="text-slate-400">
                        {currentAnalysis.record_count} records from {currentAnalysis.file_name}
                      </p>
                      {currentAnalysis.secondary_categories?.length > 0 && (
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-xs text-slate-500">Also detected:</span>
                          {currentAnalysis.secondary_categories.map((cat, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">{cat}</Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="text-right space-y-2">
                      <div>
                        <p className="text-sm text-slate-500">Analyzed</p>
                        <p className="text-white font-medium">
                          {new Date(currentAnalysis.upload_date).toLocaleString()}
                        </p>
                      </div>
                      <Button
                        onClick={() => downloadPdf(currentAnalysis.analysis_id)}
                        disabled={downloadingPdf === currentAnalysis.analysis_id}
                        className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500"
                        data-testid="download-pdf-btn"
                      >
                        {downloadingPdf === currentAnalysis.analysis_id ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4 mr-2" />
                        )}
                        Export PDF
                      </Button>
                    </div>
                  </div>

                  {/* Auto-generated PIPs */}
                  {currentAnalysis.auto_generated_pips?.length > 0 && (
                    <Alert className="mt-4 bg-amber-500/10 border-amber-500/30">
                      <AlertTriangle className="h-4 w-4 text-amber-400" />
                      <AlertTitle className="text-amber-400">Auto-Generated PIPs</AlertTitle>
                      <AlertDescription className="text-slate-300">
                        {currentAnalysis.auto_generated_pips.length} PIP(s) created due to exceeded thresholds: 
                        {currentAnalysis.auto_generated_pips.map(p => ` ${p.pip_id}`).join(',')}
                      </AlertDescription>
                    </Alert>
                  )}
                </CardContent>
              </Card>

              {/* Dynamic Analysis - NEW */}
              {renderDynamicAnalysis()}
              
              {/* Enhanced AI Insights - NEW */}
              {renderEnhancedAIInsights()}

              {/* Legacy Analysis Sections (for backwards compatibility) */}
              {renderCategoryAnalysis()}
              {renderTeamAnalysis()}
              {renderRecommendations()}

              {/* Benchmark Comparison */}
              {currentAnalysis.benchmarks_comparison?.comparisons?.length > 0 && (
                <Card className="bg-slate-900/80 border-slate-700/50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-white">
                      <PieChart className="h-5 w-5 text-blue-400" />
                      Industry Benchmark Comparison
                    </CardTitle>
                    <CardDescription>
                      {currentAnalysis.benchmarks_comparison.industry_source}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {currentAnalysis.benchmarks_comparison.comparisons.map((comp, idx) => (
                        <div 
                          key={idx}
                          className={`p-4 rounded-lg border ${
                            comp.status === 'meets' 
                              ? 'bg-emerald-500/10 border-emerald-500/30' 
                              : 'bg-amber-500/10 border-amber-500/30'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-slate-300">{comp.metric}</span>
                            {comp.status === 'meets' ? (
                              <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                            ) : (
                              <AlertTriangle className="h-5 w-5 text-amber-400" />
                            )}
                          </div>
                          <div className="flex items-end justify-between mt-2">
                            <div>
                              <p className="text-2xl font-black text-white">{comp.value}</p>
                              <p className="text-xs text-slate-500">Your Value</p>
                            </div>
                            <div className="text-right">
                              <p className="text-lg font-bold text-slate-400">{comp.benchmark}</p>
                              <p className="text-xs text-slate-500">Benchmark</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            <Card className="bg-slate-900/80 border-slate-700/50 border-dashed">
              <CardContent className="p-12 text-center">
                <BarChart3 className="h-16 w-16 text-slate-700 mx-auto mb-4" />
                <p className="text-xl font-bold text-slate-500">No Analysis Yet</p>
                <p className="text-slate-600 mt-2">Upload a report to see analysis results</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history">
          <Card className="bg-slate-900/80 border-slate-700/50">
            <CardHeader>
              <CardTitle className="flex items-center justify-between text-white">
                <div className="flex items-center gap-2">
                  <ClipboardList className="h-5 w-5 text-emerald-400" />
                  Analysis History
                </div>
                <Button variant="outline" size="sm" onClick={fetchHistory}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {analysisHistory.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="h-12 w-12 text-slate-700 mx-auto mb-4" />
                  <p className="text-slate-500">No analyses yet. Upload your first report above.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {analysisHistory.map((analysis) => {
                    const config = CATEGORY_CONFIG[analysis.detected_category] || CATEGORY_CONFIG.wounds;
                    const Icon = config.icon;
                    return (
                      <div 
                        key={analysis.analysis_id}
                        className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50 hover:border-slate-600 transition-all cursor-pointer"
                        onClick={() => loadAnalysis(analysis.analysis_id)}
                        data-testid={`history-item-${analysis.analysis_id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${config.bgColor}`}>
                              <Icon className={`h-5 w-5 ${config.textColor}`} />
                            </div>
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <Badge variant="outline" className="text-xs">{analysis.analysis_id}</Badge>
                                <Badge className={config.bgColor + ' ' + config.textColor}>
                                  {analysis.category_name}
                                </Badge>
                              </div>
                              <p className="text-white font-medium">{analysis.file_name}</p>
                              <div className="flex items-center gap-4 mt-1">
                                <p className="text-slate-500 text-sm">
                                  {analysis.record_count} records • {new Date(analysis.upload_date).toLocaleDateString()}
                                </p>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    downloadPdf(analysis.analysis_id);
                                  }}
                                  disabled={downloadingPdf === analysis.analysis_id}
                                  className="h-7 px-2 text-slate-400 hover:text-white"
                                  data-testid={`download-pdf-${analysis.analysis_id}`}
                                >
                                  {downloadingPdf === analysis.analysis_id ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <>
                                      <Download className="h-4 w-4 mr-1" />
                                      PDF
                                    </>
                                  )}
                                </Button>
                              </div>
                            </div>
                          </div>
                          <ChevronRight className="h-5 w-5 text-slate-600" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
